'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { NavigationLogo } from '../../NavigationLogo';
import { LogoVariant } from '@benzinga/logos-ui';
import { faClose } from '@fortawesome/pro-solid-svg-icons/faClose';
import { Icon } from '@benzinga/core-ui';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { MenuInterface } from '../../interfaces';
import { SimpleAccount } from './SimpleAccount';

export const SimpleHeaderMenuListItem: React.FC<MenuInterface> = ({ href, label, subnav }) => {
  const [active, setActive] = React.useState<boolean>(false);

  return (
    <MenuListItem active={active}>
      <a href={href}>{label}</a>
      {subnav && (
        <div className="ml-auto pl-5 cursor-pointer" onClick={() => setActive(!active)}>
          <Icon className="text-white simple-menu-chevron" icon={faChevronDown} />
        </div>
      )}
      {subnav && (
        <ul className={`sub-menu ${active ? 'sub-menu-active' : ''}`}>
          {subnav?.links.map(item => {
            return (
              <li className="sub-menu-item" key={item.title}>
                <a href={item.href}>{item.label}</a>
              </li>
            );
          })}
        </ul>
      )}
    </MenuListItem>
  );
};

interface SimpleHeaderMenuProps {
  menus?: MenuInterface[];
  logoVariant?: LogoVariant;
  visible?: boolean;
  onClose?: () => void;
}

export const SimpleHeaderMenu: React.FC<SimpleHeaderMenuProps> = ({ logoVariant, menus, onClose, visible = false }) => {
  const onCloseCallback = () => {
    if (onClose) {
      onClose();
    }
  };

  React.useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : '';
  }, [visible]);

  return (
    <>
      {visible && (
        <div
          className="fixed top-0 bottom-0 right-0 left-0 bg-black/50"
          onClick={onCloseCallback}
          style={{ zIndex: 90000000 }}
        />
      )}
      <MenuWrapper className={`${visible ? 'visible' : ''}`}>
        <div
          className="bg-zinc-600 text-white absolute top-0 right-0 w-7 h-6 flex items-center justify-center cursor-pointer"
          onClick={onCloseCallback}
        >
          <Icon icon={faClose} />
        </div>
        <div className="px-7 flex justify-center items-center">
          <NavigationLogo deviceType="mobile" logoVariant={logoVariant ?? 'default'} />
        </div>
        <MenuList>
          {menus?.map(item => {
            return <SimpleHeaderMenuListItem {...item} key={item.title} />;
          })}
        </MenuList>
        <FooterMenu>
          <SimpleAccount />
        </FooterMenu>
      </MenuWrapper>
    </>
  );
};

const FooterMenu = styled.div`
  color: #fff;
  padding: 50px 25px;
  text-align: center;
`;

const MenuListItem = styled.li<{ active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.11);
  flex-wrap: wrap;
  font-size: 15px;
  padding-bottom: ${({ active }) => (active ? '10px' : '0')};

  .simple-menu-chevron {
    transform: ${({ active }) => (active ? 'rotate(180deg)' : 'rotate(0)')};
  }

  a {
    padding-right: 50px;
    padding: 12px 0;
    color: white;
  }

  .sub-menu {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    width: 100%;
    transition:
      opacity 0.4s ease-in,
      max-height 1s ease-in;

    .sub-menu-item {
      font-weight: 400;
      font-size: 14px;
      padding: 8px 0;
      padding-left: 12px;
    }

    &.sub-menu-active {
      max-height: 30000px;
      opacity: 1;
      transition:
        opacity 0.4s ease-in,
        max-height 1s ease-in;
    }
  }
`;

const MenuList = styled.ul`
  width: 100%;
  padding-top: 45px;
  overflow-y: auto;
  padding-left: 25px;
  padding-right: 25px;
  max-height: 100%;
`;

const MenuWrapper = styled.div`
  background-color: #093451;
  max-width: 370px;
  width: 100%;
  left: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  position: fixed;
  z-index: 900000001;
  padding: 38px 0;
  transform: translate3d(-100%, 0, 0);
  transition: 0.4s ease-in-out;
  transition-property: transform, visibility;

  &.visible {
    transform: translate3d(0, 0, 0);
  }
`;
